/* eslint import/no-unresolved: 0 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from '@edx/frontend-platform/i18n';
import {
  ActionRow,
  Button,
  Collapsible,
  IconButton,
} from '@openedx/paragon';
import { Minus, Plus } from '@openedx/paragon/icons';

import { useModel } from '@src/generic/model-store';
import genericMessages from '@src/generic/messages';
import outlineMessages from '@src/course-home/outline-tab/messages.ts';
import SectionHeader from './SectionHeader';
import SequenceLink from './SequenceLink';
import messages from './messages';

const SectionItem = ({
  courseId,
  defaultOpen,
  expand,
  fetchSectionHasLockedContent,
  section,
  sectionId,
  sequenceData,
  upgradeUrl,
}) => {
  const intl = useIntl();

  const {
    complete,
    sequenceIds,
    title,
    hideFromTOC,
  } = section;
  const {
    courseBlocks: {
      sequences,
    },
  } = useModel('outline', courseId);

  const [open, setOpen] = useState(defaultOpen);
  const hasLockedContent = fetchSectionHasLockedContent(sectionId);

  useEffect(() => {
    setOpen(expand);
  }, [expand]);

  useEffect(() => {
    setOpen(defaultOpen);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <li>
      <Collapsible
        className="mb-2"
        styling="card-lg"
        title={(
          <SectionHeader
            {...{
              complete,
              hasLockedContent,
              hideFromTOC,
              title,
              upgradeUrl,
            }}
          />
        )}
        open={open}
        onToggle={() => setOpen(!open)}
        iconWhenClosed={(
          <IconButton
            alt={intl.formatMessage(outlineMessages.openSection)}
            iconAs={Plus}
            onClick={() => setOpen(true)}
            size="sm"
          />
        )}
        iconWhenOpen={(
          <IconButton
            alt={intl.formatMessage(genericMessages.close)}
            iconAs={Minus}
            onClick={() => { setOpen(false); }}
            size="sm"
          />
        )}
      >
        <ol className="list-unstyled my-n3 ml-n2.5 mr-n4">
          {sequenceIds.map((sequenceId, index) => (
            <SequenceLink
              key={sequenceId}
              id={sequenceId}
              courseId={courseId}
              sequence={sequences[sequenceId]}
              first={index === 0}
              sequenceData={sequenceData}
            />
          ))}
        </ol>
      </Collapsible>
      {open && hasLockedContent && (
        <ActionRow className="mr-3 mt-2.5 mb-3">
          <Button as="a" href={upgradeUrl}>
            {intl.formatMessage(messages.sectionUpgradeButtonText)}
          </Button>
        </ActionRow>
      )}
    </li>
  );
};

SectionItem.propTypes = {
  courseId: PropTypes.string.isRequired,
  defaultOpen: PropTypes.bool.isRequired,
  expand: PropTypes.bool.isRequired,
  fetchSectionHasLockedContent: PropTypes.func.isRequired,
  section: PropTypes.shape().isRequired,
  sectionId: PropTypes.string.isRequired,
  sequenceData: PropTypes.shape().isRequired,
  upgradeUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
};

export default SectionItem;
